// Teiger (AOR / geogjerder) — DELT kontrakt mellom GNÅ og SKADE.
//
// Hvorfor denne finnes: de to sidene hadde helt egne, uforenlige teigbegrep.
//
//   GNÅ  (lib/screens/geofence/geofence_models.dart):
//     • sirkel ELLER polygon, koordinater som {lat, lon}
//     • varsling «entry | exit» — når JEG (bæreren) krysser
//     • delt over et Matrix-rom som `{"t":"gna.geofence","v":1,…}`-meldinger,
//       redusert over timelinen (nyeste per id vinner, sletting = gravstein)
//
//   SKADE COP (apps/cop/src/tools/geofence.ts):
//     • KUN polygon, koordinater som [lon, lat] (GeoJSON-rekkefølge)
//     • utløsning «hostile | any» — når et SPOR av en gitt tilhørighet krysser
//     • bare i nettleserminnet: ingen persistens, ingen deling, borte ved F5
//
// Den viktigste innsikten: `entry|exit` og `hostile|any` er IKKE to varianter av
// samme felt. De svarer på ulike spørsmål — HVILKEN RETNING som varsler, og HVEM
// som utløser. En naiv «oversettelse» mellom dem ville tapt informasjon i begge
// retninger. Kontrakten bærer derfor BEGGE, ortogonalt: [retninger] og
// [gjelderFor].
//
// Tråd-kompatibilitet er ufravikelig: [parseTeigKonvolutt] leser NØYAKTIG det
// GNÅ sender i dag, og [teigKonvoluttBody] produserer nøyaktig det GNÅ leser i
// dag. En teig SKADE lager dukker altså opp i GNÅ uten en eneste endring i
// appen — og omvendt. SKADE-utvidelsene ligger i egne felt som GNÅ sin
// `fromJson` ignorerer.
import { z } from 'zod';
/** Skjema-markør i meldingskroppen. Må aldri endres — GNÅ filtrerer på den. */
export const TEIG_TYPE = 'gna.geofence';
/** Konvoluttversjon. Bumping krever at GNÅ oppdateres samtidig. */
export const TEIG_VERSJON = 1;
/** Antall segmenter en sirkel diskretiseres til for GeoJSON-rendering. */
export const SIRKEL_SEGMENTER = 64;
/** Jordradius (WGS84 middelradius) brukt i avstand/omkrets. */
const R_JORD_M = 6_371_008.8;
const Punkt = z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
});
/** Retning en kryssing kan varsle på. GNÅ kaller dette `entry`/`exit`. */
export const TeigRetning = z.enum(['inn', 'ut']);
/** Hvem som utløser varsel. SKADE-siden kalte dette `hostile | any`. */
export const TeigGjelderFor = z.enum(['alle', 'egne', 'fiendtlig', 'ukjent']);
/**
 * En teig, normalisert. Feltnavnene på tråden er GNÅ sine (snake_case der GNÅ
 * bruker det) — det er GNÅ som har den eldste, utrullede leseren, og en
 * omdøping ville brutt appen i felt.
 */
export const TeigSchema = z.object({
    id: z.string().min(1).max(200),
    name: z.string().max(200).default(''),
    shape: z.enum(['circle', 'polygon']),
    /** GNÅ-feltet: hvilken kryssing som varsler BÆREREN. Beholdt på tråden. */
    alert: z.enum(['entry', 'exit']).default('exit'),
    center: Punkt.optional(),
    radius_m: z.number().positive().max(500_000).optional(),
    polygon: z.array(Punkt).max(2000).default([]),
    active: z.boolean().default(true),
    updated_at_ms: z.number().int().nonnegative().default(0),
    author: z.string().max(200).optional(),
    // ── SKADE-utvidelser (GNÅ ignorerer ukjente felt) ────────────────────────
    /** Hvem som utløser TOC-varsel når de krysser. Default: alle spor. */
    gjelder_for: TeigGjelderFor.default('alle'),
    /** Hvilke retninger som varsler i COP. Default: begge (TOC vil vite alt). */
    retninger: z.array(TeigRetning).max(2).default(['inn', 'ut']),
    /** Hvem som opprettet teigen — avgjør om GNÅ får slette den. */
    kilde: z.enum(['gna', 'skade']).default('gna'),
});
/** En teig er brukbar først når geometrien holder. */
export function teigErGyldig(t) {
    if (t.shape === 'circle')
        return !!t.center && (t.radius_m ?? 0) > 0;
    return t.polygon.length >= 3;
}
/** Retningen GNÅ-feltet `alert` uttrykker, som normalisert retning. */
export function retningFraAlert(alert) {
    return alert === 'entry' ? 'inn' : 'ut';
}
/**
 * Leser en GNÅ-teigmelding. Returnerer null for alt annet (vanlig chat i samme
 * rom, andre skjema, ugyldig JSON) — aldri kast.
 *
 * [tsMs] brukes som gravstein-tid for slettinger; konvolutten bærer ingen tid
 * selv, så kalleren må gi hendelsestiden (Matrix `origin_server_ts`).
 */
export function parseTeigKonvolutt(body, tsMs = 0) {
    const trimmet = body.trimStart();
    if (!trimmet.startsWith('{'))
        return null;
    let raw;
    try {
        raw = JSON.parse(trimmet);
    }
    catch {
        return null;
    }
    if (!raw || typeof raw !== 'object')
        return null;
    const m = raw;
    if (m['t'] !== TEIG_TYPE)
        return null;
    if (m['op'] === 'delete') {
        const id = typeof m['id'] === 'string' ? m['id'] : '';
        return id ? { op: 'delete', id, tsMs } : null;
    }
    const parsed = TeigSchema.safeParse(m['fence']);
    if (!parsed.success)
        return null;
    const teig = parsed.data;
    // Uten tid fra teigen selv bruker vi hendelsestiden, ellers ville reduksjonen
    // aldri kunne skille to endringer på samme id.
    if (!teig.updated_at_ms && tsMs)
        teig.updated_at_ms = tsMs;
    return { op: 'upsert', teig };
}
/** Bygger meldingskroppen GNÅ leser. Speiler `GeofenceEnvelope.encode()`. */
export function teigKonvoluttBody(h) {
    if (h.op === 'delete') {
        return JSON.stringify({ t: TEIG_TYPE, v: TEIG_VERSJON, op: 'delete', id: h.id });
    }
    const t = h.teig;
    // Kun felt GNÅ faktisk skriver, i samme form — pluss SKADE-utvidelsene, som
    // GNÅ sin fromJson hopper over. Utelatte valgfrie felt utelates helt (GNÅ
    // gjør det samme), så en runde GNÅ→SKADE→GNÅ er byte-identisk der det teller.
    const fence = {
        id: t.id,
        name: t.name,
        shape: t.shape,
        alert: t.alert,
        ...(t.center ? { center: { lat: t.center.lat, lon: t.center.lon } } : {}),
        ...(t.radius_m !== undefined ? { radius_m: t.radius_m } : {}),
        ...(t.polygon.length ? { polygon: t.polygon.map((p) => ({ lat: p.lat, lon: p.lon })) } : {}),
        active: t.active,
        updated_at_ms: t.updated_at_ms,
        ...(t.author ? { author: t.author } : {}),
        gjelder_for: t.gjelder_for,
        retninger: t.retninger,
        kilde: t.kilde,
    };
    return JSON.stringify({ t: TEIG_TYPE, v: TEIG_VERSJON, op: 'upsert', fence });
}
/**
 * Reduserer tidsordnede hendelser til gjeldende teigsett. Nyeste per id vinner;
 * en sletting er en gravstein som holder teigen borte til en NYERE upsert
 * kommer. Speiler GNÅ sin reduksjon, slik at begge sider ser samme sett.
 */
export function reduserTeiger(hendelser) {
    const gjeldende = new Map();
    const slettetVed = new Map();
    for (const h of hendelser) {
        if (h.op === 'upsert') {
            const t = h.teig;
            const forrige = gjeldende.get(t.id);
            if (forrige && forrige.updated_at_ms > t.updated_at_ms)
                continue;
            const slettet = slettetVed.get(t.id);
            if (slettet !== undefined && t.updated_at_ms <= slettet)
                continue;
            gjeldende.set(t.id, t);
            slettetVed.delete(t.id);
        }
        else {
            const forrige = gjeldende.get(h.id);
            if (forrige && forrige.updated_at_ms > h.tsMs)
                continue;
            slettetVed.set(h.id, h.tsMs);
            gjeldende.delete(h.id);
        }
    }
    return [...gjeldende.values()].sort((a, b) => a.id.localeCompare(b.id));
}
// ── Geometri (ETT sted for koordinatrekkefølgen) ───────────────────────────
/**
 * Teigen som lukket ring i GeoJSON-rekkefølge `[lon, lat]`.
 *
 * Dette er den ENESTE stedet konverteringen {lat,lon} → [lon,lat] skjer. Alle
 * feilene av typen «kartet viste teigen i Somalia» kommer av at den regelen var
 * spredt utover; her er den én funksjon med tester.
 *
 * Sirkler diskretiseres geodetisk (ikke som en flat lat/lon-ellipse), så en
 * 5 km-sirkel på 60°N ikke blir en ovalt strukket flekk.
 */
export function teigTilRing(t, segmenter = SIRKEL_SEGMENTER) {
    if (t.shape === 'circle') {
        const c = t.center;
        const r = t.radius_m;
        if (!c || !r)
            return [];
        const n = Math.max(8, Math.min(360, Math.floor(segmenter)));
        const lat0 = (c.lat * Math.PI) / 180;
        const lon0 = (c.lon * Math.PI) / 180;
        const vinkelavstand = r / R_JORD_M;
        const ring = [];
        for (let i = 0; i < n; i++) {
            const brg = (2 * Math.PI * i) / n;
            const lat = Math.asin(Math.sin(lat0) * Math.cos(vinkelavstand) +
                Math.cos(lat0) * Math.sin(vinkelavstand) * Math.cos(brg));
            const lon = lon0 + Math.atan2(Math.sin(brg) * Math.sin(vinkelavstand) * Math.cos(lat0), Math.cos(vinkelavstand) - Math.sin(lat0) * Math.sin(lat));
            ring.push([(lon * 180) / Math.PI, (lat * 180) / Math.PI]);
        }
        ring.push(ring[0]);
        return ring;
    }
    if (t.polygon.length < 3)
        return [];
    const ring = t.polygon.map((p) => [p.lon, p.lat]);
    const første = ring[0];
    const siste = ring[ring.length - 1];
    if (første[0] !== siste[0] || første[1] !== siste[1])
        ring.push([...første]);
    return ring;
}
/** Geodetisk avstand (haversine) i meter. Samsvarer med GNÅ sin latlong2. */
export function avstandM(a, b) {
    const φ1 = (a.lat * Math.PI) / 180;
    const φ2 = (b.lat * Math.PI) / 180;
    const dφ = φ2 - φ1;
    const dλ = ((b.lon - a.lon) * Math.PI) / 180;
    const h = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
    return 2 * R_JORD_M * Math.asin(Math.min(1, Math.sqrt(h)));
}
/**
 * Er punktet inne i teigen? Sirkel: geodetisk avstand ≤ radius. Polygon:
 * strålekasting i plan lat/lon — samme algoritme som GNÅ, så de to sidene ALDRI
 * er uenige om hvem som er innenfor.
 */
export function punktITeig(t, p) {
    if (!teigErGyldig(t))
        return false;
    if (t.shape === 'circle')
        return avstandM(t.center, p) <= t.radius_m;
    const v = t.polygon;
    let inne = false;
    for (let i = 0, j = v.length - 1; i < v.length; j = i++) {
        const yi = v[i].lat, xi = v[i].lon;
        const yj = v[j].lat, xj = v[j].lon;
        const krysser = yi > p.lat !== yj > p.lat &&
            p.lon < ((xj - xi) * (p.lat - yi)) / (yj - yi) + xi;
        if (krysser)
            inne = !inne;
    }
    return inne;
}
/** Skal en kryssing gi varsel i COP? Begge akser må stemme. */
export function teigUtloser(t, affiliation, retning) {
    if (!t.active)
        return false;
    if (!t.retninger.includes(retning))
        return false;
    switch (t.gjelder_for) {
        case 'alle': return true;
        case 'egne': return affiliation === 'friend';
        case 'fiendtlig': return affiliation === 'hostile';
        case 'ukjent': return affiliation === 'unknown';
    }
}
/** GeoJSON-Feature til COP-rendering (polygon + sentroide for etiketten). */
export function teigTilGeoJson(t) {
    const ring = teigTilRing(t);
    if (ring.length < 4)
        return null;
    return {
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [ring] },
        properties: {
            id: t.id,
            name: t.name,
            shape: t.shape,
            gjelder_for: t.gjelder_for,
            kilde: t.kilde,
            active: t.active,
        },
    };
}
/** Sentroide av ringen (etikett-plassering), eller null for ugyldig teig. */
export function teigSentroide(t) {
    if (t.shape === 'circle' && t.center)
        return [t.center.lon, t.center.lat];
    const ring = teigTilRing(t);
    if (ring.length < 4)
        return null;
    // Siste punkt er duplikat av første — hold det utenfor snittet.
    const pts = ring.slice(0, -1);
    let x = 0, y = 0;
    for (const [lon, lat] of pts) {
        x += lon;
        y += lat;
    }
    return [x / pts.length, y / pts.length];
}
/**
 * Bygger en teig fra en COP-tegnet GeoJSON-ring `[[lon,lat],…]`.
 * Kaster aldri; returnerer null når ringen har for få punkter.
 */
export function teigFraRing(ring, opts) {
    const punkter = [];
    for (const par of ring) {
        if (!Array.isArray(par) || par.length < 2)
            continue;
        const [lon, lat] = par;
        if (!Number.isFinite(lon) || !Number.isFinite(lat))
            continue;
        punkter.push({ lat, lon });
    }
    // En lukket ring har første == siste; teigen lagres uten duplikatet.
    if (punkter.length >= 2) {
        const f = punkter[0];
        const s = punkter[punkter.length - 1];
        if (f.lat === s.lat && f.lon === s.lon)
            punkter.pop();
    }
    if (punkter.length < 3)
        return null;
    return TeigSchema.parse({
        id: opts.id,
        name: opts.name,
        shape: 'polygon',
        alert: 'exit',
        polygon: punkter,
        active: true,
        updated_at_ms: opts.tsMs ?? 0,
        ...(opts.author ? { author: opts.author } : {}),
        gjelder_for: opts.gjelderFor ?? 'alle',
        retninger: opts.retninger ?? ['inn', 'ut'],
        kilde: 'skade',
    });
}
//# sourceMappingURL=geofence.js.map